import { can } from '@atlas-control/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, type LinkProps } from '@tanstack/react-router';
import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { dashboardQuery } from '@/features/runs/api';
import { api } from '@/lib/api';
import { RunStatusBadge, StageBar } from '@/features/runs/components';
import { actionLabel } from '@/lib/audit-labels';
import { useMe } from '@/lib/auth';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/')({
  component: HomePage,
});

/** The work queue: what needs a person right now. */
function HomePage() {
  const { user } = useMe();
  const data = useQuery({ ...dashboardQuery, enabled: can(user.role, 'runs.read') });
  const d = data.data;

  const tiles: { label: string; n: number | undefined; link: LinkProps; urgent?: boolean }[] = [
    {
      label: 'Intakes to review',
      n: d?.intakesToReview,
      link: { to: '/forms', search: { reviewed: 'no' } },
    },
    {
      label: 'Paid, not started',
      n: d?.readyToStart,
      link: { to: '/runs', search: { group: 'to_start' } },
    },
    {
      label: 'Final review',
      n: d?.finalReview,
      link: { to: '/runs', search: { group: 'in_progress' } },
    },
    {
      label: 'Emails scheduled',
      n: d?.emailsScheduled,
      link: { to: '/emails', search: { status: 'scheduled' } },
    },
    {
      label: 'Emails failed',
      n: d?.emailsFailed,
      link: { to: '/emails', search: { status: 'failed' } },
      urgent: true,
    },
  ];

  return (
    <>
      <PageHeader title={`Hello, ${user.name.split(' ')[0]}`} />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        {tiles.map((t) => (
          <Link key={t.label} {...t.link} className="block">
            <Card className="h-full py-4 transition-colors hover:bg-accent/50">
              <CardContent className="px-4">
                {t.n === undefined ? (
                  <Skeleton className="h-8 w-10" />
                ) : (
                  <p
                    className={cn(
                      'text-3xl font-semibold tabular-nums',
                      t.n > 0 && t.urgent && 'text-danger',
                      t.n === 0 && 'text-muted-foreground',
                    )}
                  >
                    {t.n}
                  </p>
                )}
                <p className="mt-1 text-sm text-muted-foreground">{t.label}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Runs in progress</CardTitle>
          </CardHeader>
          <CardContent>
            {d?.inProgress.length === 0 && (
              <p className="text-sm text-muted-foreground">No runs in progress.</p>
            )}
            <ul className="divide-y">
              {d?.inProgress.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/runs/$runId"
                    params={{ runId: r.id }}
                    className="flex flex-wrap items-center justify-between gap-3 py-2 hover:bg-accent/40"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{r.company}</span>
                      <RunStatusBadge status={r.status} />
                    </span>
                    <StageBar
                      done={r.stagesDone}
                      total={r.stagesTotal}
                      reached={r.stageReached?.name ?? null}
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">My recent activity</CardTitle>
          </CardHeader>
          <CardContent>
            <MyActivity />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/** The signed-in user's own last ten actions. */
function MyActivity() {
  const q = useQuery({
    queryKey: ['my-activity'],
    queryFn: () =>
      api<{ entries: { id: string; at: string; action: string; entityType: string }[] }>(
        '/me/activity',
      ),
  });
  if (q.isPending) return <Skeleton className="h-20" />;
  const entries = q.data?.entries ?? [];
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">Nothing yet.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {entries.map((e) => (
        <li key={e.id} className="flex justify-between gap-3">
          <span>{actionLabel(e.action)}</span>
          <span className="text-muted-foreground">{formatRelative(e.at)}</span>
        </li>
      ))}
    </ul>
  );
}
